import {ExtensionContext} from '@github-ui/copilot-markdown/ExtensionContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import {type ComponentProps, type ReactNode, use, useEffect, useRef} from 'react'

import type {TableBadgeStats} from './stats'

interface TableScrollWrapperProps extends ComponentProps<'div'> {
  statsRaw: string
  children?: ReactNode
}

function parseStats(raw: string): TableBadgeStats | null {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const stats = parsed as TableBadgeStats
    if (typeof stats.totalColumns !== 'number') return null
    if (typeof stats.badgedColumns !== 'number') return null
    if (typeof stats.rejectionReasons !== 'object' || stats.rejectionReasons === null) return null
    return stats
  } catch {
    return null
  }
}

// Fires `dotcom_chat.table_render` once per wrapper instance, gated on
// `isStreaming` so we don't fire on every streaming chunk.
function useTableBadgeTelemetry(statsRaw: string) {
  const {isStreaming, chatMode} = use(ExtensionContext)
  const firedRef = useRef(false)

  useEffect(() => {
    if (isStreaming) return
    if (firedRef.current) return
    const stats = parseStats(statsRaw)
    if (!stats) return
    firedRef.current = true
    sendEvent('dotcom_chat.table_render', {
      target: 'COMPACT_TABLE',
      action: 'rendered',
      mode: chatMode,
      totalColumns: stats.totalColumns,
      badgedColumns: stats.badgedColumns,
      reasonHasLink: stats.rejectionReasons.hasLink,
      reasonMixedValues: stats.rejectionReasons.mixedValues,
      reasonNoRealValues: stats.rejectionReasons.noRealValues,
      reasonNoEligibleValues: stats.rejectionReasons.noEligibleValues,
    })
  }, [isStreaming, statsRaw, chatMode])
}

export function TableScrollWrapper({statsRaw, children, ...rest}: TableScrollWrapperProps) {
  useTableBadgeTelemetry(statsRaw)
  return <div {...rest}>{children}</div>
}
